import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IUser } from "../../type";
import "./index.css";
import pensil from "./pensil.svg";
import logoutIcon from "./logout.svg";
import lighting from "../../img/lighting.svg";
import application from "../../img/application.svg";
import profile_icon from "../../img/profileColor.svg";
import serebro_icon from "./serebro-icon.svg";
import {
  getDeferredPrompt,
  isEmbeddedInIframe,
  isLikelyIOS,
  isPwaInstalled,
  isStandaloneMode,
} from "../../utils/pwaInstallPrompt";
import { subscribeForPush } from "../../utils/pushSubscription";
import { resetCookieSession } from "../../api/user";

interface IProps {
  user: IUser;
  coin: number;
  setUser: (v: IUser) => void;
}

function getYearString(years: number) {
  years = Math.floor(years);
  if (years % 10 === 1 && years % 100 !== 11) {
    return years + " год";
  } else if (
    years % 10 >= 2 &&
    years % 10 <= 4 &&
    (years % 100 < 10 || years % 100 >= 20)
  ) {
    return years + " года";
  } else {
    return years + " лет";
  }
}

export function Profile(props: IProps) {
  const nav = useNavigate();
  const [showInstallBlock, setShowInstallBlock] = useState(false);
  const [canUseInstallPrompt, setCanUseInstallPrompt] = useState(false);
  const [isInstalledApp, setIsInstalledApp] = useState(false);
  const [isInFrame, setIsInFrame] = useState(false);
  const [supportsPush, setSupportsPush] = useState(false);
  const [isRefreshingPush, setIsRefreshingPush] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const [isResettingCookies, setIsResettingCookies] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const lastPwaState = useRef<{ hasDeferred: boolean; installed: boolean } | null>(
    null
  );

  const isIOS = isLikelyIOS();
  const isIosSafari = (() => {
    if (!isIOS || typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/i.test(ua);
    return isSafari;
  })();

  /** iOS Safari (вкладка) часто не отдаёт PushManager — нельзя связывать с ним кнопку «Очистить кэш». */
  const canClearAppCache =
    typeof window !== "undefined" &&
    ("serviceWorker" in navigator || "caches" in window);

  useEffect(() => {
    const refreshState = () => {
      const deferred = getDeferredPrompt();
      const installed = isPwaInstalled() || isStandaloneMode();
      const inFrame = isEmbeddedInIframe();
      const canPrompt = !!deferred && !installed && !inFrame;

      setIsInstalledApp(installed);
      setIsInFrame(inFrame);
      setShowInstallBlock(
        !inFrame &&
          (
            !installed || // сценарий установки (до установки)
            (installed && (
              isLikelyIOS() || // iOS-гайд/статус
              (
                typeof window !== "undefined" &&
                "serviceWorker" in navigator &&
                "PushManager" in window &&
                typeof Notification !== "undefined"
              ) // installed Android/другие: кнопка/статус push
            ))
          )
      );
      setCanUseInstallPrompt(canPrompt);
      setSupportsPush(
        typeof window !== "undefined" &&
          "serviceWorker" in navigator &&
          "PushManager" in window &&
          typeof Notification !== "undefined"
      );

      const hasDeferred = !!deferred;
      if (
        !lastPwaState.current ||
        lastPwaState.current.hasDeferred !== hasDeferred ||
        lastPwaState.current.installed !== installed
      ) {
        lastPwaState.current = { hasDeferred, installed };
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.log("[PWA] Profile state", { hasDeferred, installed, inFrame });
        }
      }
    };

    refreshState();

    const onBefore = () => refreshState();
    const onInstalled = () => refreshState();

    window.addEventListener("beforeinstallprompt", onBefore as any);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("focus", onBefore as any);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore as any);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("focus", onBefore as any);
    };
  }, []);

  const onInstall = async () => {
    const deferredPrompt = getDeferredPrompt();
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setShowInstallBlock(false);
    }
  };

  const onEnablePush = async () => {
    setIsRefreshingPush(true);
    try {
      // В standalone обновляем PWA-эндпоинт, иначе обновляем web-эндпоинт.
      const source = isStandaloneMode() ? "pwa" : "web";
      await subscribeForPush(source, { forceResubscribe: true });
    } catch {
      // ignore
    } finally {
      setIsRefreshingPush(false);
    }
  };

  const onClearCache = async () => {
    if (isClearingCache) return;
    setIsClearingCache(true);
    try {
      // 1) Снимаем регистрацию service worker (чтобы он не подложил старый кэш обратно)
      if (typeof navigator !== "undefined" && "serviceWorker" in navigator) {
        try {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        } catch {
          // ignore
        }
      }

      // 2) Удаляем Cache Storage (статические файлы, стили, и т.д.)
      if (typeof window !== "undefined" && "caches" in window) {
        try {
          const keys = await caches.keys();
          // Чистим только PWA-кэш нашего приложения (стили/статические файлы).
          // Это НЕ трогает localStorage/sessionStorage.
          const toDelete = keys.filter((k) => k.startsWith("wisewife-cache-"));
          await Promise.all(toDelete.map((k) => caches.delete(k)));
        } catch {
          // ignore
        }
      }

      // 3) Перезагрузка с «анти-кэшем», чтобы точно забрать новые файлы
      const url = new URL(window.location.href);
      url.searchParams.set("cache_bust", String(Date.now()));
      window.location.replace(url.toString());
    } finally {
      setIsClearingCache(false);
    }
  };

  const clearClientCookiesAndStorage = async () => {
    try {
      const cookieNames = document.cookie
        .split(";")
        .map((part) => part.split("=")[0]?.trim())
        .filter(Boolean);
      for (const name of cookieNames) {
        document.cookie = `${name}=; path=/; max-age=0`;
      }
    } catch {
      // ignore
    }
    try {
      localStorage.clear();
    } catch {
      // ignore
    }
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }
  };

  const onResetCookies = async () => {
    if (isResettingCookies) return;
    setIsResettingCookies(true);
    try {
      await resetCookieSession();
    } catch {
      // ignore network problems and continue with local cleanup
    }
    await clearClientCookiesAndStorage();
    window.location.assign("/login?session=manual_reset");
  };

  const editClick = () => {
    nav("/profile/edit");
  };

  const onLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      // ignore
    }
    try {
      localStorage.clear();
    } catch {
      // ignore
    }
    try {
      sessionStorage.clear();
    } catch {
      // ignore
    }
    window.location.assign("/login?session=logout");
  };

  let ageInYears = "";
  if (props.user.birthdate) {
    let age =
      (Date.now() - props.user.birthdate.getTime()) /
      (1000 * 60 * 60 * 24 * 365);
    ageInYears = getYearString(age);
  }

  return (
    <section className="section mainPage flex flex-column sectionUser">
      <div className="sectionUser-content">
        <div className="headerUser flex flex-column">
          <div className="headerUser-avatar">
            <div className="avatarProfile">
              <img
                src={
                  (props.user.avatarLink &&
                    props.user.avatarLink !== "no-avatar" &&
                    props.user.avatarLink) ||
                  "/avatar.png"
                }
                className="avatarProfile-image"
                alt=""
              />
            </div>
            <div>
              <p className="semiBoldFontHeaderProfile">{props.user.name}</p>
              <p className="normalMediumBoldFontHeader">{ageInYears || "-"}</p>
            </div>
          </div>
          <div className="headerUser-actions">
            <button
              type="button"
              className="profile-icon-button"
              onClick={editClick}
              aria-label="Редактировать профиль"
            >
              <img src={pensil} alt="" />
            </button>
            <button
              type="button"
              className="profile-icon-button"
              onClick={onLogout}
              aria-label="Выйти из приложения"
              disabled={isLoggingOut}
            >
              <img src={logoutIcon} alt="" />
            </button>
          </div>
        </div>
        {/* <div className="conteinerUser">
          <div className="conteinerUser-text">
            <p className="normalFont textcolorbraun">
              Заполните полностью Ваш профиль и получите вознаграждение
            </p>
            <div className="progressbar-profile">
              <div className="progressbar-profile_progress-all">
                <div
                  className="progressbar-profile_progress"
                  style={
                    {
                      width: (persent * 100).toFixed(0) + "%",
                    } as React.CSSProperties
                  }
                ></div>
              </div>
              <div className="progressbar-profile_percent">
                {" "}
                {(persent * 100).toFixed(0)}%
              </div>
            </div>
          </div>
        </div> */}
        <div className="conteinerUser conteinerUserBody">
          <div className="conteinerUser-text">
            <div>
              <p className="normalFont textcolorbraun">Телефон</p>
              <p className="normalMediumBoldFont">{props.user.phone}</p>
            </div>
            <div>
              {" "}
              <p className="normalFont textcolorbraun">E-mail</p>
              <p className="normalMediumBoldFont">{props.user.email}</p>
            </div>
            <div>
              <p className="normalFont textcolorbraun">Дата рождения</p>
              <p className="normalMediumBoldFont">
                {props.user.birthdate?.toLocaleDateString() || "-"}
              </p>
            </div>
            <div>
              <p className="normalFont textcolorbraun">Город (Где живете?)</p>
              <p className="normalMediumBoldFont">{props.user.city || "-"}</p>
            </div>
            <div>
              {" "}
              <p className="normalFont textcolorbraun">О себе</p>
              <p className="normalMediumBoldFont">{props.user.about || "-"}</p>
            </div>
          </div>
        </div>

        <div className="conteinerUser">
          <div className="conteinerUser-text">
            <div className="coin">
              <img src={serebro_icon} alt=""/>
              <span className="coin-text">{props.coin}</span>
            </div>
            <p className="normalLightFont textcolorbraun">Виртуальная карта</p>
          </div>
        </div>

        {showInstallBlock && (
          <div
            style={{
              width: "100%",
              padding: "12px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 10,
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              alignSelf: "center",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            {isIOS && !isInstalledApp ? (
              <p
                className="normalLightFont textcolorbraun"
                style={{ margin: 0, lineHeight: 1.45, width: "100%", alignSelf: "stretch" }}
              >
                {isIosSafari
                  ? "Наше приложение поддерживает установку PWA на рабочий стол: нажмите «Поделиться» → «На экран Домой» → «Добавить»."
                  : "Для установки на iPhone/iPad откройте сайт в Safari, затем: «Поделиться» → «На экран Домой» → «Добавить»."}
              </p>
            ) : null}
            {!isIOS && canUseInstallPrompt && (
              <button
                type="button"
                className="installAppButton"
                onClick={onInstall}
                style={{ display: "block" }}
              >
                Установить приложение
              </button>
            )}
          </div>
        )}

        {!isInFrame && (supportsPush || canClearAppCache) && (
          <div
            style={{
              width: "100%",
              padding: "0 16px",
              marginTop: 10,
              marginBottom: 10,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              alignSelf: "center",
              marginLeft: "auto",
              marginRight: "auto",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {supportsPush && (
              <button
                type="button"
                className="installAppButton"
                onClick={onEnablePush}
                disabled={isRefreshingPush}
                style={{ display: "block" }}
              >
                {isRefreshingPush ? "Обновляем подписку..." : "Обновить подписку на уведомления"}
              </button>
            )}

            {canClearAppCache && (
              <button
                type="button"
                className="installAppButton installAppButton-outline"
                onClick={onClearCache}
                disabled={isClearingCache}
                style={{
                  display: "block",
                  alignSelf: "center",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                {isClearingCache ? "Очищаем кэш..." : "Очистить кэш"}
              </button>
            )}
          </div>
        )}
        {!isInFrame && (
          <div
            style={{
              width: "100%",
              padding: "0 16px",
              marginTop: 4,
              marginBottom: 12,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              boxSizing: "border-box",
              alignSelf: "center",
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <button
              type="button"
              className="installAppButton installAppButton-danger"
              onClick={onResetCookies}
              disabled={isResettingCookies}
              style={{
                display: "block",
                alignSelf: "center",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {isResettingCookies ? "Сбрасываем..." : "Сброс Cookie"}
            </button>
          </div>
        )}
      </div>
      <div className="profile_footer">
        <Link to="/rating">
          <div className="footer_tab">
            <img src={lighting} alt=""/>
            <span className="textMenu">Комьюнити</span>
          </div>
        </Link>
        <Link to="/">
          <div className="footer_tab">
            <img src={application} alt=""/>
            <span className="textMenu">Главная</span>
          </div>
        </Link>
        <Link to="/profile">
          <div className="footer_tab">
            <img src={profile_icon} alt=""/>
            <span className="textMenu">Профиль</span>
          </div>
        </Link>
      </div>
    </section>
  );
}
